import Component from "../component";
import { AssetFeatureResponse } from "../../types/stores";
import {
    getReadableAddress,
    getReadableDistance,
    getOpeningLabel,
    getOpeningWeekList,
    checkSameTimesForAllDays
} from "../../helpers/stores";
import { WoosmapApiClient } from "../../services/woosmap_api";
import { getLocale, getLocaleLang } from "../../helpers/locale";
import { getConfig } from "../../configuration/config";

export interface IStoresListComponent {
    stores?: AssetFeatureResponse[];
    nearbyLocation?: woosmap.map.LatLngLiteral;
    query?: string;
    expandedView?: boolean;
    selectedStore?: AssetFeatureResponse;
    highlightStore?: boolean;
    showOnSuccess?:boolean;
}

export enum StoresListComponentEvents {
    STORES_CHANGED = "stores_changed",
    LOCALITY_CHANGED = "locality_changed",
    QUERY_CHANGED = "query_changed",
    STORE_SELECTED = "store_selected",
    STORE_MOUSEENTER = "store_mouseenter",
    STORE_MOUSELEAVE = "store_mouseleave",
    CTA_TARIFFS_CLICKED = "cta_tariffs_clicked",
    USER_LOCATION_CHANGED = 'user_location_changed',
}

export default class StoresListComponent extends Component<IStoresListComponent> {
    private api!: WoosmapApiClient;

    init(): void {
        this.api = new WoosmapApiClient({ apiKey: getConfig().map.woosmapPublicKey });
        this.$element = document.createElement("ul");
        this.$element.setAttribute("role", "list");
        this.$target.appendChild(this.$element);
        this.on(StoresListComponentEvents.LOCALITY_CHANGED, () => {
            this.searchStores();
        });
        this.on(StoresListComponentEvents.QUERY_CHANGED, () => {
            this.searchStores();
        });
    }

    render(): void {
        if (this.state && this.$element) {
            if (this.state.stores === undefined && this.state.selectedStore) {
                this.setState({ stores: [this.state.selectedStore], expandedView: true }, true);
            }
            else if (this.state.stores?.length === 1 && this.state.selectedStore) {
                this.setState({ stores: [this.state.selectedStore], expandedView: this.state.expandedView }, true);
            }
            if (this.state.stores?.length) {
                const storesElements: HTMLLIElement[] = this.state.stores?.map(
                    (store: AssetFeatureResponse) => {
                        const properties = store.properties;
                        const $storeElement: HTMLLIElement = document.createElement("li");
                        $storeElement.className = this.state.highlightStore ? "summaryStore summaryStore--non-interactive" : "summaryStore";
                        $storeElement.setAttribute("role", "listitem");
                        $storeElement.setAttribute("tabindex", this.state.highlightStore ? "-1" : "0");
                        $storeElement.setAttribute("aria-label", `${store.properties.name} ${getLocale().time.at} ${getReadableAddress(store.properties.address)} , ${getReadableDistance(store.properties.distance)}`)
                        $storeElement.dataset.storeId = store.properties.store_id;
                        const $storeName: HTMLDivElement = this.getStoreNameAddress(store);
                        const $ctaElements: HTMLDivElement = this.getCTAElements(store);
                        $storeElement.appendChild($storeName);
                        properties.open &&
                            $storeElement.appendChild(this.getStoreTimings(store));
                        (properties.open &&
                            properties.open.open_now &&
                            properties.weekly_opening &&
                            !properties.open.current_slice['all-day']) &&
                            $storeElement.appendChild(this.getStoreTimingsRange(store));

                        properties.distance && $storeElement.appendChild(this.getDistance(store));
                        $storeElement.appendChild($ctaElements);
                        if (!this.state.highlightStore) {
                            $storeElement.addEventListener("click", () => {
                                this.emit(StoresListComponentEvents.STORE_SELECTED, store);
                            });
                            $storeElement.addEventListener('mouseenter', () => {
                                this.emit(StoresListComponentEvents.STORE_MOUSEENTER, store);
                            });
                            $storeElement.addEventListener('mouseleave', () => {
                                this.emit(StoresListComponentEvents.STORE_MOUSELEAVE, store);
                            });
                            $storeElement.addEventListener('keydown', (event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    this.emit(StoresListComponentEvents.STORE_SELECTED, store);
                                }
                            });
                        }
                        return $storeElement;
                    }
                );
                this.$element.replaceChildren(...storesElements);


            } else {
                if (this.state.stores) {
                    const message = getLocale().stores.messages.emptyHeader;
                    const $emptyResults: HTMLDivElement = document.createElement("div");
                    $emptyResults.innerHTML = `
                    <div class="summaryStore__empty">
                        <div>${message}</div>
                    </div>`;
                    this.$element.replaceChildren($emptyResults);
                } else {
                    this.$element.replaceChildren();
                }
            }
        }
    }

    searchStores(): void {
        if (this.state.nearbyLocation) {
            let params = Object.assign({}, getConfig().search.searchAPIParameters, {
                lat: this.state.nearbyLocation.lat,
                lng: this.state.nearbyLocation.lng,
            });
            if (this.state.query) {
                params = Object.assign({}, params, {
                    query: this.state.query,
                });
            }
            this.api.stores
                .searchStores(params)
                .then((response) => {
                    let storesList = response?.features.map((store) => store);
                    if (storesList.length > 0) {
                        storesList = this.filterStoresByRadius(storesList);
                    }
                    this.setState({ stores: storesList });
                    this.emit(StoresListComponentEvents.STORES_CHANGED, storesList, this.state.nearbyLocation);
                })
                .catch((exception) => {
                    console.error(exception);
                    this.setState({ stores: [] });
                });
        }
    }
    filterStoresByRadius(storesList: AssetFeatureResponse[]): AssetFeatureResponse[] {
        let result: AssetFeatureResponse | undefined;
        let filteredStores: AssetFeatureResponse[] = [];
        const storesWithinDefaultRadius = storesList.filter(item => item.properties && item.properties.distance && item.properties.distance <= getConfig().search.searchRadius);
        filteredStores = storesWithinDefaultRadius;
        if (storesWithinDefaultRadius.length === 0) {
            result = storesList.find(item => item.properties && item.properties.distance && item.properties.distance > getConfig().search.searchRadius);
            if (result) {
                filteredStores.push(result);
            }
        }
        return filteredStores;
    }
    toggleActive($elementToToggle: HTMLDivElement): void {
        $elementToToggle.classList.toggle("active");
        const content = $elementToToggle.nextElementSibling as HTMLDivElement;
        let panelOpened = false;
        if (content?.style.maxHeight && content?.style.maxHeight != "0px") {
            panelOpened = false;
            content.style.maxHeight = "0px";
        } else {
            panelOpened = true;
            content.style.maxHeight = content.scrollHeight + "px";
        }
    }
    getStoreNameAddress(store: AssetFeatureResponse): HTMLDivElement {
        const $element = <HTMLDivElement>document.createElement("div");
        $element.className = "summaryStore__container";
        $element.innerHTML = `<div class="summaryStore__details"><div class="summaryStore__openinghours">${this.getStoreCluster(store)}</div><div class="summaryStore__name">${store.properties.name}</div>        
        ${store.properties.address ? `<div class="summaryStore__address">${getReadableAddress(store.properties.address)}</div>` : ""}</div>`;
        // $element.setAttribute("aria-hidden", "true");
        const $IconElement = <HTMLDivElement>document.createElement("div");
        $IconElement.className = "buttonIcon directionsButton";
        $IconElement.setAttribute("role", "button");
        $IconElement.setAttribute("tabindex", "0");
        $IconElement.setAttribute("aria-label", getLocale().stores.cta["directions"]);
        // $IconElement.setAttribute("aria-hidden", "true");
        $IconElement.addEventListener("click", () => this.getGoogleDirections(store));
        $IconElement.addEventListener('keydown', function (event) {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                $IconElement.click();
            }
        });
        $element.appendChild($IconElement);
        return $element
    }
    getStoreTimings(store: AssetFeatureResponse): HTMLDivElement {
        const $element = <HTMLDivElement>document.createElement("div");
        $element.className = this.state.expandedView ? "collapsible active" : "collapsible";
        $element.setAttribute("aria-expanded", String(this.state.expandedView || false));
        $element.setAttribute("role", "region");
        $element.innerHTML = `<span class="summaryStore__openinghours">${getOpeningLabel(store.properties)}</span>
        ${this.openingHoursFoldable(store) ? `<div class="collapsibleIcon" role="button" tabindex="0" aria-expanded="false" aria-label="${getLocale().time.openingHoursTitle}"></div>` 
    : ""}`;
        if (this.openingHoursFoldable(store)) {
            $element.addEventListener("click", () => this.toggleActive($element));
            $element.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    const target = event.target as HTMLElement;
                    const expanded = target.getAttribute("aria-expanded") === "true";
                    target.setAttribute("aria-expanded", (!expanded).toString());
                    $element.click();
                }
            });
        }
        return $element;
    }
    getStoreTimingsRange(store: AssetFeatureResponse): HTMLDivElement {
        const $element = <HTMLDivElement>document.createElement("div");
        $element.className = "collapsibleContent";
        $element.innerHTML = `${store.properties.weekly_opening ? `<div class="summaryStore__listItems" aria-hidden="true">${getOpeningWeekList(store.properties.weekly_opening)}</div>` : ""}`;
        if (this.state.selectedStore) {
            if (store.properties.store_id === this.state.selectedStore.properties.store_id) {
                if (store.properties.weekly_opening && !checkSameTimesForAllDays(store.properties.weekly_opening)) {
                    $element.style.maxHeight = `252px`;
                }
            }
        }
        if (this.state.highlightStore && this.state.selectedStore) {
            if (store.properties.store_id === this.state.selectedStore.properties.store_id) {
                if (store.properties.weekly_opening && !checkSameTimesForAllDays(store.properties.weekly_opening)) {
                    $element.style.maxHeight = `0px`;
                }
            }
        }
        return $element;
    }
    getDistance(store: AssetFeatureResponse): HTMLDivElement {
        const $element = <HTMLDivElement>document.createElement("div");
        $element.className = "summaryStore__distance";
        $element.innerHTML = `${getReadableDistance(store.properties.distance)}`;
        $element.setAttribute("aria-hidden", "true");
        return $element
    }
    getCTAElements(store: AssetFeatureResponse): HTMLDivElement {
        const $element = <HTMLDivElement>document.createElement("div");
        $element.className = "summaryStore__ctacontainer";

        const $clubDtlsBtn: HTMLButtonElement = document.createElement("button");
        $clubDtlsBtn.className = "summaryStore__ctaclubdetails";
        $clubDtlsBtn.type = "button";
        $clubDtlsBtn.setAttribute("tabindex", "0");
        $clubDtlsBtn.innerHTML = getLocale().stores.cta["club details"];
        $clubDtlsBtn.setAttribute("aria-label",  getLocale().stores.cta["club details"]);
        // $clubDtlsBtn.setAttribute("aria-hidden", "true");
        $clubDtlsBtn.title = getLocale().stores.cta["club details"];
        const isENLocale = getLocaleLang().toUpperCase().startsWith("EN");
        const clubUrl = isENLocale && store.properties.user_properties?.ENwebsiteURL
            ? String(store.properties.user_properties.ENwebsiteURL)
            : store.properties.contact?.website;
        if (clubUrl) {
            $clubDtlsBtn.addEventListener("click", () => {
                window.open(clubUrl, '_parent');
            });
            $clubDtlsBtn.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    $clubDtlsBtn.click();
                }
            });
        }

        if (!this.state.highlightStore) {
            $element.appendChild($clubDtlsBtn);
        }

        if (store.properties.user_properties
            && store.properties.user_properties?.checkout
            && store.properties.user_properties?.checkout === "True") {
            const $clubPlansBtn: HTMLButtonElement = document.createElement("button");
            $clubPlansBtn.className = "summaryStore__ctaclubtariff";
            $clubPlansBtn.type = "button";
            $clubPlansBtn.setAttribute("tabindex", "0");
            // $clubPlansBtn.setAttribute("aria-hidden", "true");
            $clubPlansBtn.innerHTML = getLocale().stores.cta["view tariffs"];
            $clubPlansBtn.addEventListener("click", () => {
                this.emit(StoresListComponentEvents.CTA_TARIFFS_CLICKED, store);
            });
            $clubPlansBtn.addEventListener('keydown', function (event) {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    $clubPlansBtn.click();
                }
            });
            if(!this.state.showOnSuccess) {
                $element.appendChild($clubPlansBtn);
            }
        }

        return $element;
    }
    getGoogleDirections(store: AssetFeatureResponse): void {
        let destination = store.geometry.coordinates[1] + ',' + store.geometry.coordinates[0];
        const storeName: string = store.properties.user_properties?.name as string;
        const storeAddress = getReadableAddress(store.properties.address);
        if (storeName) {
            destination = `${storeName}`
        }
        if (storeAddress) {
            destination = `${destination}","${storeAddress}`
        }
        window.open(`http://www.google.com/maps/dir/?api=1&destination=${destination}`);
    }
    openingHoursFoldable(store: AssetFeatureResponse): boolean {
        if (store.properties.open &&
            store.properties.open.open_now &&
            store.properties.open.current_slice.start &&
            store.properties.weekly_opening &&
            !checkSameTimesForAllDays(store.properties.weekly_opening)) {
            return true;
        }
        return false;
    }
    getStoreCluster(store: AssetFeatureResponse): string {
        if (store.properties.user_properties && store.properties.user_properties.cluster) {
            return store.properties.user_properties?.cluster as string;
        }
        else {
            return "";
        }
    }
}
